/** Marketing copy for WorkLoop landing page */

export const HERO_TYPE_LINES = [
  'Your Connecting Partner to everyone.',
  'Chat, tasks & attendance — together.',
  'One workspace. Infinite connection.',
  'Channels, DMs & threads in real time.',
  'Tasks, workflows & reminders linked to chat.',
  'Desktop sync — offline-ready on every desk.',
];

export const HERO_SLIDES = [
  {
    id: 'intro',
    kicker: 'One workspace for modern teams',
    headline: 'Messaging, tasks & attendance — together',
    subtitle:
      'WorkLoop brings messaging, task tracking, attendance, calls, and announcements into a single secure platform — web, desktop, iPhone, and Android.',
    points: [
      'Channels, DMs, threads, and mentions',
      'Daily tasks, reminders, and workflows',
      'Attendance, presence, and team analytics',
      'Web, desktop, iOS, and Android',
    ],
    cta: 'Explore services',
    ctaHref: '#services',
    accent: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #0ea5e9 100%)',
  },
  {
    id: 'messaging',
    kicker: 'Real-time collaboration',
    headline: 'Channels, DMs & threads',
    subtitle:
      'WebSocket-powered chat with mentions, reactions, file sharing, threads, and read receipts — everything your team expects from a modern messenger.',
    points: [
      'Public and private channels with admins',
      'Rich composer, drafts, and link previews',
      'Search, saved messages, and notifications',
      'Live typing, presence, and read receipts',
    ],
    cta: 'See all features',
    ctaHref: '#features',
    accent: '#0ea5e9',
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 50%, #6366f1 100%)',
  },
  {
    id: 'work',
    kicker: 'Work that stays connected',
    headline: 'Tasks, workflows & reminders',
    subtitle:
      'Delegate tasks from chat, map messages to work items, set daily reminders, and track productivity — without switching tools.',
    points: [
      'Pick today and delegated task views',
      'Map chat messages to tasks or workflows',
      'Recurring reminders with live popups',
      'CUMIN issue tracking from WorkLoop',
    ],
    cta: 'View services',
    ctaHref: '#services',
    accent: '#f97316',
    gradient: 'linear-gradient(135deg, #f97316 0%, #ec4899 50%, #6366f1 100%)',
  },
  {
    id: 'desktop',
    kicker: 'Desktop · Web · Sync',
    headline: 'Local sync & offline-ready',
    subtitle:
      'Electron desktop with Realm local database: faster cold starts, bootstrap + delta sync, and offline-friendly message queue.',
    points: [
      'Local Realm cache per signed-in user',
      'Bootstrap once, then 60s delta sync',
      'WebSocket events written to local DB',
      'Offline-first outbound message queue',
    ],
    cta: 'Download desktop',
    ctaHref: '#download',
    accent: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981 0%, #0ea5e9 50%, #6366f1 100%)',
  },
];

export const AGENDA_ITEMS = [
  {
    title: 'Connect instantly',
    body: 'Replace scattered chats with organized channels, DMs, and live presence — so everyone knows who is available and what is happening.',
    icon: '💬',
  },
  {
    title: 'Turn talk into action',
    body: 'Tasks, reminders, workflows, and CUMIN integration link conversations to deliverables your team can track and delegate.',
    icon: '✅',
  },
  {
    title: 'Run the day together',
    body: 'Attendance check-in, leave, shared status, team timelines, and analytics keep HR and managers aligned with daily operations.',
    icon: '📅',
  },
  {
    title: 'Stay secure at scale',
    body: 'JWT auth, org signup, admin approvals, announcements with acknowledgement, and role-based access for enterprise-ready control.',
    icon: '🔒',
  },
];

export const SERVICES = [
  {
    title: 'Team messaging',
    summary: 'Channels, DMs, threads, mentions, reactions, and file sharing.',
    points: [
      'Public & private channels with member admin roles',
      'Direct messages, self-notes, and thread replies',
      'Rich composer with emoji, drafts, and link previews',
      'Real-time updates via WebSocket',
    ],
    color: '#6366f1',
  },
  {
    title: 'Tasks & reminders',
    summary: 'Daily tasks panel, delegation, and message-linked work.',
    points: [
      'Pick today, in-progress, and delegated task views',
      'Reminders with recurrence and popup notifications',
      'Map chat messages to tasks or workflows',
      'Productivity analytics snippet',
    ],
    color: '#0ea5e9',
  },
  {
    title: 'Attendance & presence',
    summary: 'Check-in, breaks, leave, and team visibility.',
    points: [
      'Log to work, logout summary, and short breaks',
      'Share attendance with teammates',
      'Leave requests and manager approvals',
      'Team attendance analytics dashboard',
    ],
    color: '#f97316',
  },
  {
    title: 'Calls & calendar',
    summary: 'LiveKit video, meetings, and appointments.',
    points: [
      'In-app video calls with screen share',
      'Meeting timeline cards in chat',
      'Personal and channel calendar panels',
      'RSVP and availability slots',
    ],
    color: '#ec4899',
  },
  {
    title: 'Announcements & admin',
    summary: 'Org broadcasts, templates, and governance.',
    points: [
      'Targeted announcements with acknowledgement',
      'Admin user approval and password reset review',
      'Organization signup and system channels',
      'Activity tracker and app update config',
    ],
    color: '#8b5cf6',
  },
  {
    title: 'Desktop sync',
    summary: 'Electron + Realm local database on macOS, Windows, Linux.',
    points: [
      'Bootstrap and delta sync from Spring API',
      'WebSocket events written to local Realm',
      'Offline-first outbound message queue',
      'Faster reads from local cache',
    ],
    color: '#10b981',
  },
];

export const FEATURE_GROUPS = [
  {
    name: 'Messaging',
    color: '#6366f1',
    items: [
      'Send messages with rich text and attachments',
      '@user and #channel mentions',
      'Typing indicators and read receipts',
      'Edit, delete, copy, forward, and reply in thread',
      'In-channel search with date and sender filters',
      'Saved messages and global saved page',
    ],
  },
  {
    name: 'Channels & DMs',
    color: '#0ea5e9',
    items: [
      'Create public or private channels',
      'Direct messages and self-chat notes',
      'System channels (General, Attendance, Announcements)',
      'Channel members panel and channel admins',
      'Shared files, images, links, and resource groups',
    ],
  },
  {
    name: 'Tasks & workflows',
    color: '#f97316',
    items: [
      'Task management page with filters',
      'Delegate tasks and link to chat messages',
      'Workflow assignment and custom tags on messages',
      'Management metrics by work item type',
      'CUMIN issue creation from tasks',
    ],
  },
  {
    name: 'Attendance',
    color: '#10b981',
    items: [
      'Check-in briefing and logout summary',
      'Short breaks and day activity timeline',
      'Apply leave and pending approvals',
      'Share attendance with teammates',
      'Team attendance analytics',
    ],
  },
  {
    name: 'Notifications & presence',
    color: '#ec4899',
    items: [
      'Browser push and in-app toast notifications',
      'Draggable notification bubble with unread list',
      'Manual status (Available, Away, DND, Leave)',
      'Idle activity-based presence',
      'Mention and task delegation alerts',
    ],
  },
  {
    name: 'Security & platform',
    color: '#8b5cf6',
    items: [
      'JWT login, OTP verification, password reset',
      'Single active browser tab enforcement',
      'Dark and light mode',
      'Desktop apps for macOS, Windows, and Linux',
      'iOS App Store and Android Google Play apps',
      'Local Realm sync with 60s delta safety-net',
    ],
  },
];

export const TEAM = [
  {
    role: 'Product & engineering',
    name: 'Tech Elliptica',
    blurb: 'We design and build WorkLoop — from real-time chat and task flows to desktop Realm sync and org admin tooling.',
  },
  {
    role: 'Platform',
    name: 'WorkLoop core',
    blurb: 'Spring Boot backend, MongoDB source of truth, STOMP WebSocket fan-out, and a shared sync contract for web and desktop clients.',
  },
  {
    role: 'Customer success',
    name: 'Support & onboarding',
    blurb: 'Help your organization adopt WorkLoop with account setup, training, and ongoing support through our support portal.',
  },
];
